// Monoline icons. 16px viewbox, 1.5 stroke. Lightweight, custom-drawn.
// Ported from yeni-design/src/icons.jsx.
import type { ReactElement, ReactNode } from 'react';

export interface IconProps {
  size?: number;
  className?: string;
  strokeWidth?: number;
  fill?: string;
}

interface BaseIconProps extends IconProps {
  d?: string;
  children?: ReactNode;
}

export const Icon = ({
  d,
  size = 16,
  className = '',
  strokeWidth = 1.5,
  fill = 'none',
  children,
}: BaseIconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill={fill}
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    {d ? <path d={d} /> : children}
  </svg>
);

export type IconComponent = (props: IconProps) => ReactElement;

export const I: Record<string, IconComponent> = {
  Home: (p) => (
    <Icon {...p}>
      <path d="M2.5 7L8 2.5 13.5 7v6a.5.5 0 01-.5.5H3a.5.5 0 01-.5-.5V7z" />
      <path d="M6.5 13.5V9.5h3v4" />
    </Icon>
  ),
  Live: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="8" r="2" />
      <path d="M4.5 4.5a5 5 0 000 7M11.5 4.5a5 5 0 010 7M2.5 2.5a8 8 0 000 11M13.5 2.5a8 8 0 010 11" />
    </Icon>
  ),
  Cash: (p) => (
    <Icon {...p}>
      <rect x="2" y="4" width="12" height="8" rx="1" />
      <circle cx="8" cy="8" r="1.5" />
      <path d="M4 6.5v3M12 6.5v3" />
    </Icon>
  ),
  Bank: (p) => (
    <Icon {...p}>
      <path d="M2 6L8 2.5 14 6" />
      <path d="M3 6v6M6 6v6M10 6v6M13 6v6" />
      <path d="M2 13.5h12" />
    </Icon>
  ),
  Debt: (p) => (
    <Icon {...p}>
      <path d="M3 13l2-2 2 1 4-5 2 1" />
      <circle cx="11" cy="3.5" r="1" />
    </Icon>
  ),
  Recv: (p) => (
    <Icon {...p}>
      <path d="M3 3l2 2 2-1 4 5 2-1" />
      <circle cx="13" cy="13" r="1" />
    </Icon>
  ),
  ChequeOut: (p) => (
    <Icon {...p}>
      <rect x="2" y="3.5" width="12" height="9" rx="1" />
      <path d="M4.5 7.5h4M4.5 9.5h2.5" />
      <path d="M9.5 10.5l1.5-1.5 1.5 1.5" />
    </Icon>
  ),
  ChequeIn: (p) => (
    <Icon {...p}>
      <rect x="2" y="3.5" width="12" height="9" rx="1" />
      <path d="M4.5 7.5h4M4.5 9.5h2.5" />
      <path d="M9.5 9l1.5 1.5L12.5 9" />
    </Icon>
  ),
  Stock: (p) => (
    <Icon {...p}>
      <path d="M2 5l6-3 6 3-6 3-6-3z" />
      <path d="M2 5v6l6 3 6-3V5" />
      <path d="M8 8v6" />
    </Icon>
  ),
  Cost: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M9.5 6.5h-2a1 1 0 000 2h1a1 1 0 010 2h-2.5M8 5.5v.5M8 10.5v.5" />
    </Icon>
  ),
  Fire: (p) => (
    <Icon {...p}>
      <path d="M8 13.5c2.5 0 4-1.7 4-4 0-2-1-3-2-3.5.5 1.5-.5 2.5-1.5 2-.5-1 0-3-2-4.5 1 2.5-2.5 3.5-2.5 6 0 2.3 1.5 4 4 4z" />
    </Icon>
  ),
  Expense: (p) => (
    <Icon {...p}>
      <path d="M3 3v10h10" />
      <path d="M5.5 10.5l2-2 2 1 3-3.5" />
    </Icon>
  ),
  Profit: (p) => (
    <Icon {...p}>
      <path d="M3 12l3-3 2 2 4-5" />
      <path d="M9.5 6h2.5v2.5" />
    </Icon>
  ),

  Search: (p) => (
    <Icon {...p}>
      <circle cx="7" cy="7" r="4" />
      <path d="M10 10l3 3" />
    </Icon>
  ),
  Filter: (p) => (
    <Icon {...p}>
      <path d="M2.5 3.5h11l-4 5v4l-3-1.5v-2.5l-4-5z" />
    </Icon>
  ),
  Bell: (p) => (
    <Icon {...p}>
      <path d="M4 11.5V8a4 4 0 018 0v3.5l1 1.5H3l1-1.5z" />
      <path d="M6.5 13.5a1.5 1.5 0 003 0" />
    </Icon>
  ),
  Help: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M6.5 6.5a1.5 1.5 0 013 0c0 1-1.5 1-1.5 2M8 11.2v.1" />
    </Icon>
  ),
  Chevron: (p) => (
    <Icon {...p}>
      <path d="M6 4l4 4-4 4" />
    </Icon>
  ),
  ChevronDown: (p) => (
    <Icon {...p}>
      <path d="M4 6l4 4 4-4" />
    </Icon>
  ),
  ChevronUp: (p) => (
    <Icon {...p}>
      <path d="M4 10l4-4 4 4" />
    </Icon>
  ),
  ArrowUp: (p) => (
    <Icon {...p}>
      <path d="M8 13V3M4 7l4-4 4 4" />
    </Icon>
  ),
  ArrowDown: (p) => (
    <Icon {...p}>
      <path d="M8 3v10M4 9l4 4 4-4" />
    </Icon>
  ),
  ArrowRight: (p) => (
    <Icon {...p}>
      <path d="M3 8h10M9 4l4 4-4 4" />
    </Icon>
  ),
  Plus: (p) => (
    <Icon {...p}>
      <path d="M8 3v10M3 8h10" />
    </Icon>
  ),
  X: (p) => (
    <Icon {...p}>
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
    </Icon>
  ),
  Check: (p) => (
    <Icon {...p}>
      <path d="M3 8l3 3 7-7" />
    </Icon>
  ),
  Eye: (p) => (
    <Icon {...p}>
      <path d="M1.5 8s2-5 6.5-5 6.5 5 6.5 5-2 5-6.5 5S1.5 8 1.5 8z" />
      <circle cx="8" cy="8" r="2" />
    </Icon>
  ),
  EyeOff: (p) => (
    <Icon {...p}>
      <path d="M2 2l12 12" />
      <path d="M6 5.5C7 4.7 7.5 4.5 8 4.5c4.5 0 6.5 3.5 6.5 3.5s-.6 1-1.7 2M11 11.5c-.9.5-1.8.7-3 .7-4.5 0-6.5-4-6.5-4S2.5 6 4 5" />
    </Icon>
  ),
  Mail: (p) => (
    <Icon {...p}>
      <rect x="2" y="3.5" width="12" height="9" rx="1" />
      <path d="M2 4.5l6 4 6-4" />
    </Icon>
  ),
  Lock: (p) => (
    <Icon {...p}>
      <rect x="3" y="7" width="10" height="6" rx="1" />
      <path d="M5.5 7V5a2.5 2.5 0 015 0v2" />
    </Icon>
  ),
  Calendar: (p) => (
    <Icon {...p}>
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <path d="M2 6h12M5 1.5v3M11 1.5v3" />
    </Icon>
  ),
  Download: (p) => (
    <Icon {...p}>
      <path d="M8 2v8M5 7l3 3 3-3M3 13h10" />
    </Icon>
  ),
  Print: (p) => (
    <Icon {...p}>
      <path d="M4 6V2.5h8V6M4 11H2.5v-4a1 1 0 011-1h9a1 1 0 011 1v4H12" />
      <rect x="4" y="9" width="8" height="4.5" />
    </Icon>
  ),
  Refresh: (p) => (
    <Icon {...p}>
      <path d="M3 8a5 5 0 018.5-3.5L13 6M13 3v3h-3M13 8a5 5 0 01-8.5 3.5L3 10M3 13v-3h3" />
    </Icon>
  ),
  More: (p) => (
    <Icon {...p}>
      <circle cx="3.5" cy="8" r=".7" fill="currentColor" />
      <circle cx="8" cy="8" r=".7" fill="currentColor" />
      <circle cx="12.5" cy="8" r=".7" fill="currentColor" />
    </Icon>
  ),
  Menu: (p) => (
    <Icon {...p}>
      <path d="M2.5 4h11M2.5 8h11M2.5 12h11" />
    </Icon>
  ),
  Sidebar: (p) => (
    <Icon {...p}>
      <rect x="2" y="3" width="12" height="10" rx="1" />
      <path d="M6 3v10" />
    </Icon>
  ),
  User: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="6" r="2.5" />
      <path d="M3 13.5c.5-2.2 2.5-3.5 5-3.5s4.5 1.3 5 3.5" />
    </Icon>
  ),
  Building: (p) => (
    <Icon {...p}>
      <rect x="3" y="2" width="10" height="12" rx="1" />
      <path d="M5.5 5h2M8.5 5h2M5.5 7.5h2M8.5 7.5h2M5.5 10h2M8.5 10h2" />
    </Icon>
  ),
  Inbox: (p) => (
    <Icon {...p}>
      <path d="M2.5 8.5l1.5-5h8l1.5 5v4a1 1 0 01-1 1h-9a1 1 0 01-1-1v-4z" />
      <path d="M2.5 8.5h3l1 1.5h3l1-1.5h3" />
    </Icon>
  ),
  Circle: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="8" r="3.5" fill="currentColor" stroke="none" />
    </Icon>
  ),
  Phone: (p) => (
    <Icon {...p}>
      <path d="M3.5 2.5h2l1 3-1.5 1a7 7 0 003.5 3.5l1-1.5 3 1v2a1 1 0 01-1 1A9 9 0 012.5 3.5a1 1 0 011-1z" />
    </Icon>
  ),
  Doc: (p) => (
    <Icon {...p}>
      <path d="M4 2h5l3 3v8.5a.5.5 0 01-.5.5h-7a.5.5 0 01-.5-.5V2.5a.5.5 0 01.5-.5z" />
      <path d="M9 2v3h3" />
    </Icon>
  ),
  Settings: (p) => (
    <Icon {...p}>
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1.5v1.5M8 13v1.5M3.5 3.5l1 1M11.5 11.5l1 1M1.5 8h1.5M13 8h1.5M3.5 12.5l1-1M11.5 4.5l1-1" />
    </Icon>
  ),
};
